import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { CsvExportParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useExtendedExportSettings } from '../../../../components/AgTable/exportToExcelDefs';
import { SectionPlaceholder } from '../../../../components/SectionPlaceholder/SectionPlaceholder';
import { InvestorCashFlowRow } from '../../../../schemas/InvestorCashFlows.schema';
import { sanitizeAsFileName } from '../../../../util/stringUtils';
import { NoDataMessage2 } from '../../../CompanyProfiles/components/Messages/HelpMessage';
import { SubSection } from '../../../CompanyProfiles/Summary/components/SubSection';
import { InvestorCashFlowsGrid } from './InvestorCashFlowsGrid';
import { InvestorCashFlowsToolbar } from './InvestorCashFlowsToolbar';
import { investorCashFlowsTypeJ, useInvestorCashFlows, useResetInvestorCashFlowsFilters } from './state';

export const NoInvestorCashFlowsMessage = 'No investor cash flows for the selected filters.';

// Currency and date cells export as displayed; the default callback exports their raw values.
const ExportFormattedColIds = new Set(['amount', 'signedAmount', 'transactionDate']);

// Sits in FPSummary's 1rem grid like its siblings, without SubSection's own vertical margin.
const sectionStyle: CSSProperties = { margin: 0 };

interface IInvestorCashFlowsSectionProps {
  fundId: number;
}

/** FD-2562: the fund's contribution and distribution ledger by investor type, with a CSV export. */
export function InvestorCashFlowsSection({ fundId }: IInvestorCashFlowsSectionProps) {
  const { rows, fundName, fetchedRange, hasSlp, isLoading, isFetching, hasError, retry } =
    useInvestorCashFlows(fundId);
  const type = useAtomValue(investorCashFlowsTypeJ);
  const resetFilters = useResetInvestorCashFlowsFilters();
  const [gridApi, setGridApi] = useState<GridApi<InvestorCashFlowRow> | null>(null);

  // The filters are this section's own state; leaving the profile must not carry them to the next visit.
  useEffect(() => resetFilters, [resetFilters]);

  // Named from the envelope, not the filters, so the file always matches the rows it was built from;
  // the type is the current selection because the export honours the client-side filter.
  const fileName = useMemo(
    () =>
      sanitizeAsFileName(
        `investor-cash-flows-${fundName}-${type}-${fetchedRange.from ?? 'start'}-${fetchedRange.to ?? 'end'}`
      ),
    [fundName, type, fetchedRange]
  );
  const { exportParams } = useExtendedExportSettings<InvestorCashFlowRow>(ExportFormattedColIds, fileName);

  const onGridReady = useCallback((event: GridReadyEvent<InvestorCashFlowRow>) => setGridApi(event.api), []);
  // The grid unmounts whenever the fetch has no rows; a destroyed api must not be kept.
  const onGridPreDestroyed = useCallback(() => setGridApi(null), []);
  const onDownload = useCallback(() => gridApi?.exportDataAsCsv(exportParams), [gridApi, exportParams]);

  return (
    <SubSection
      title='Investor Cash Flows'
      withFallbacks
      style={sectionStyle}
      actions={
        <Button
          variant='contained'
          color='secondary'
          size='medium'
          startIcon={<DownloadIcon />}
          // Disabled through a refetch too: the grid still shows the previous rows, which the name would misdescribe.
          disabled={isLoading || isFetching || rows.length === 0}
          onClick={onDownload}
        >
          Download CSV
        </Button>
      }
    >
      <InvestorCashFlowsToolbar hasSlp={hasSlp} />
      <Body
        exportParams={exportParams}
        gridApi={gridApi}
        hasError={hasError}
        isLoading={isLoading}
        onGridPreDestroyed={onGridPreDestroyed}
        onGridReady={onGridReady}
        retry={retry}
        rows={rows}
      />
    </SubSection>
  );
}

interface IBodyProps {
  exportParams: CsvExportParams;
  gridApi: GridApi<InvestorCashFlowRow> | null;
  hasError: boolean;
  isLoading: boolean;
  onGridPreDestroyed: () => void;
  onGridReady: (event: GridReadyEvent<InvestorCashFlowRow>) => void;
  retry: () => void;
  rows: InvestorCashFlowRow[];
}

function Body({
  exportParams,
  gridApi,
  hasError,
  isLoading,
  onGridPreDestroyed,
  onGridReady,
  retry,
  rows,
}: IBodyProps) {
  if (isLoading) {
    return <SectionPlaceholder label='Loading investor cash flows…' />;
  }
  if (hasError) {
    // A rejected fetch is cached per request key, so without a retry the section stays broken.
    return <SectionPlaceholder label='Investor cash flows could not be loaded.' onRetry={retry} />;
  }
  if (rows.length === 0) {
    return <NoDataMessage2 message={NoInvestorCashFlowsMessage} />;
  }
  return (
    <InvestorCashFlowsGrid
      exportParams={exportParams}
      gridApi={gridApi}
      onGridPreDestroyed={onGridPreDestroyed}
      onGridReady={onGridReady}
      rows={rows}
    />
  );
}
